import { ReactNode } from "react";
import { ExternalLink } from "lucide-react";
import { cn } from "~/lib/utils";

interface SettingsRowProps {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  // Optional inline value shown beside the title (e.g. a dataset id).
  value?: ReactNode;
  // When provided, the whole row opens this URL in a new tab, with a
  // hover-revealed external-link indicator to the left of the action control.
  href?: string;
  // The action control (button) rendered on the right.
  children?: ReactNode;
}

/**
 * One labeled action row on an entity's Settings page: an optional icon, a
 * title with optional description/value, and a right-aligned action control.
 */
export const SettingsRow = ({
  icon,
  title,
  description,
  value,
  href,
  children,
}: SettingsRowProps) => {
  const left = (
    <div className="flex min-w-0 flex-1 items-center gap-3">
      {icon}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-foreground">{title}</span>
          {value != null && (
            <span className="truncate font-mono text-xs text-muted-foreground">{value}</span>
          )}
        </div>
        {description && (
          <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{description}</p>
        )}
      </div>
    </div>
  );

  return (
    <div
      className={cn(
        "group flex items-center justify-between gap-4 px-4 py-3",
        href && "hover:bg-accent/50"
      )}
    >
      {href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className="min-w-0 flex-1">
          {left}
        </a>
      ) : (
        left
      )}
      {(href || children) && (
        <div className="flex shrink-0 items-center gap-1">
          {href && (
            <ExternalLink
              className="size-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
              aria-hidden="true"
            />
          )}
          {children}
        </div>
      )}
    </div>
  );
};
